import { DefaultTheme, type Interpolation, css } from 'styled-components'

import { ITEM_SHADOW, MEDIA_QUERY } from '../../../styles'
import { ButtonProps } from './index'
import { ButtonHeight, ButtonThemeType } from './types'


const resolveButtonTheme = (buttonTheme: ButtonThemeType, theme: DefaultTheme) => {
  const colorMap: Record<ButtonThemeType, Interpolation<{}>> = {
    orange: css`
      background-color: ${theme.colors.common.regular.orange};
      color: ${theme.colors.font.white};
      border: 2px solid ${theme.colors.common.regular.orange};

      &:hover {
        border: 2px solid ${theme.colors.common.dark.orange};
        background-color: ${theme.colors.common.dark.orange};
        box-shadow: 0 4px 12px -4px rgba(210, 82, 0, 0.6);
      }
    `,
    blue: css`
      background-color: ${theme.colors.common.regular.brightBlue};
      color: ${theme.colors.font.white};
      border: 2px solid ${theme.colors.common.regular.brightBlue};

      &:hover {
        border: 2px solid ${theme.colors.common.dark.brightBlue};
        background-color: ${theme.colors.common.dark.brightBlue};
        box-shadow: 0 4px 12px -4px rgba(26, 103, 210, 0.6);
      }
    `,
    transparent: css`
      background-color: transparent;
      color: ${theme.colors.font.link};
      border: 2px solid ${theme.colors.input.border};

      &:hover {
        background-color: rgba(29, 105, 236, 0.06);
        box-shadow: ${ITEM_SHADOW};
      }
    `,
    transparentDark: css`
      background-color: transparent;
      color: ${theme.colors.font.white};
      border: 2px solid rgba(255, 255, 255, 0.3);

      &:hover {
        border: 2px solid rgba(255, 255, 255, 0.4);
        background-color: rgba(255, 255, 255, 0.05);
        box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.3);
      }
    `,
    blank: css`
      background-color: transparent;
      color: ${theme.colors.font.link};
      border: 2px solid transparent;

      &:hover {
        background-color: #f4f8fe;
        box-shadow: none;
      }
    `
  }

  return colorMap[buttonTheme]
}

const resolveDisabledStyles = (transparent: boolean, theme: DefaultTheme): Interpolation<{}> => {
  return css`
    cursor: not-allowed;
    border: 2px solid ${theme.colors.background.disabled};
    background: ${transparent ? 'transparent' : theme.colors.background.disabled};
    color: ${transparent ? theme.colors.background.disabled : theme.colors.font.white};
  `
}

export type ButtonStyleProps = Pick<ButtonProps, 'buttonTheme' | 'disabled' | 'textWrap' | 'minWidth' | 'buttonHeight'>

const buttonHeightMap: Record<ButtonHeight, number> = {
  small: 44,
  medium: 50
}

export const buttonStyles = css<ButtonStyleProps>`
  transition: 0.3s;
  height: ${({ buttonHeight = 'medium' }) => `${buttonHeightMap[buttonHeight]}px`};
  min-width: ${({ minWidth }) => minWidth ?? '100px'};
  border-radius: 999px;
  border-width: 2px;
  font-weight: 600;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  text-wrap: ${({ textWrap }) => textWrap ?? 'wrap'};
  ${({ buttonTheme, theme, disabled }) => {
    return disabled
      ? resolveDisabledStyles(buttonTheme === 'transparent', theme)
      : resolveButtonTheme(buttonTheme ?? 'blue', theme)
  }};
  @media ${MEDIA_QUERY.MOBILE} {
    width: 100%;
  }
`
